import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { fmtMoney, fmtKm } from "@/lib/format";
import { resolveImageUrl, optimizedImageUrl } from "@/lib/api";

const isNewArrival = (v) => {
  if (!v.created_at) return false;
  const created = new Date(v.created_at);
  return Date.now() - created.getTime() < 14 * 24 * 60 * 60 * 1000;
};

export const VehicleCard = ({ vehicle, sold = false }) => {
  const img = vehicle.images?.[0];
  const onSale = !sold && vehicle.sale_price && vehicle.sale_price < vehicle.price;
  const meta = [fmtKm(vehicle.mileage), vehicle.transmission, vehicle.drivetrain].filter(Boolean).join(" · ");

  return (
    <article
      className="group bg-card border border-border overflow-hidden hover:border-primary/60 transition-colors duration-200 flex flex-col rounded-md"
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {img ? (
          <img
            src={optimizedImageUrl(resolveImageUrl(img), 800)}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ""} exterior photo`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm font-mono uppercase">Photo Coming Soon</div>
        )}
        {sold && (
          <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
            <span className="bg-white/95 text-foreground font-display font-black uppercase tracking-widest text-sm px-4 py-1.5 rounded-sm" data-testid={`sold-overlay-${vehicle.id}`}>
              Sold
            </span>
          </div>
        )}
        {!sold && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {vehicle.status === "pending" && (
              <Badge className="bg-amber-500 text-black border-0 font-bold uppercase text-[10px] tracking-wider rounded-sm">Pending Sale</Badge>
            )}
            {vehicle.featured && (
              <Badge className="bg-primary text-primary-foreground border-0 font-bold uppercase text-[10px] tracking-wider rounded-sm">Featured</Badge>
            )}
            {isNewArrival(vehicle) && (
              <Badge variant="outline" className="bg-white/90 border-border text-foreground uppercase text-[10px] tracking-wider rounded-sm">New Arrival</Badge>
            )}
            {onSale && (
              <Badge className="bg-green-600 text-white border-0 font-bold uppercase text-[10px] tracking-wider rounded-sm">Sale</Badge>
            )}
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg font-bold text-foreground leading-snug mb-1 line-clamp-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        {vehicle.trim && <p className="text-sm text-muted-foreground mb-2">{vehicle.trim}</p>}

        {sold ? (
          <p className="font-display text-xl font-black text-muted-foreground uppercase tracking-wide mb-1">Sold</p>
        ) : (
          <div className="flex items-baseline gap-2 mb-1">
            {onSale ? (
              <>
                <p className="font-display text-2xl font-black text-primary">{fmtMoney(vehicle.sale_price)}</p>
                <p className="text-sm text-muted-foreground line-through">{fmtMoney(vehicle.price)}</p>
              </>
            ) : (
              <p className="font-display text-2xl font-black text-primary">{fmtMoney(vehicle.price)}</p>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground font-mono mb-4">{meta}</p>

        <div className="mt-auto">
          <Link
            to={`/vehicles/${vehicle.id}`}
            className={sold ? "btn-themed-outline w-full" : "btn-themed w-full"}
            data-testid={`view-details-btn-${vehicle.id}`}
          >
            {sold ? "View Vehicle" : "View Details"}
          </Link>
        </div>
      </div>
    </article>
  );
};
